import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IExerciseQuestionType } from './exercises.interface';
import { UserService } from './user.service';
import { ExerciseService } from './exercise.service';
import { Answers } from './answers';
import { getDisplaySolution } from './utils/display.utils';
import { RealtimeSessionConfig, VoiceTranscript, RawVoiceSegment } from './services/realtime-voice.service';

export interface DialogAttemptStatus {
  attempts: Array<{
    attempt: number;
    answerId?: string;
    completed: boolean;
    completedAt?: string;
    score?: number | null;
    createdAt?: string;
    assignmentId?: string | null;
  }>;
  attemptsUsed: number;
  attemptsRemaining: number;
  maxAttempts: number;
  latestAttempt: number | null;
  limitReached: boolean;
  kind: 'dialog' | 'interactive';
}

export interface VoiceSegmentUploadPayload {
  audioBase64: string;
  sampleRate: number;
  startMs?: number;
  endMs?: number;
  chunkId?: string;
  language?: string;
}

export interface VoiceSegmentTranscriptionResponse {
  transcription: string;
  rawSegments?: RawVoiceSegment[];
  transcriptionStats?: {
    segmentCount?: number;
    averageNoSpeechProb?: number;
    maxNoSpeechProb?: number;
    averageCompressionRatio?: number;
    maxCompressionRatio?: number;
  };
  chunkId?: string;
  startMs?: number;
  endMs?: number;
  sampleRate?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AnswerService {

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private exerciseService: ExerciseService
  ) { }

  public async submitAnswers(questions: IExerciseQuestionType[], assignmentId?: string, exerciseId?: string): Promise<void> {
    let user = await this.userService.getUser();
    if (!user) {
      try {
        const apiUser = await this.userService.getUserApi();
        if (apiUser) {
          await this.userService.setCurrentUser(apiUser);
          user = apiUser;
        }
      } catch (error) {
        console.error('[AnswerService] Failed to fetch user from API:', error);
      }
    }
    
    // Use provided exerciseId or fall back to activeExercise
    const effectiveExerciseId = exerciseId || this.exerciseService.activeExercise?._id;
    
    if (!user) {
      console.error('[AnswerService] Missing user');
      return;
    }
    
    if (!effectiveExerciseId) {
      console.error('[AnswerService] Missing exerciseId - neither provided nor activeExercise available');
      return;
    }

    const transformedQuestions = questions.map(question => {
      // Try multiple fallbacks for userAnswer
      const userAnswer = question.userAnswer || 
                        (question as any).currentSolution ||
                        (question as any).selectedAnswer;
      
      // For dialog exercises, use currentSolution as solution
      const solution = question.solution || 
                      (question as any).currentSolution ||
                      userAnswer;
      
      // Log missing data for debugging but don't fail silently
      if (!question._id) {
        console.error('[AnswerService] Missing question._id:', question);
        return null;
      }
      
      // isCorrect should be defined, but default to false if missing
      const isCorrect = question.isCorrect !== undefined ? question.isCorrect : false;
      
      // Allow userAnswer and solution to be any value (including empty string or 0)
      if (userAnswer === undefined && userAnswer === null) {
        console.warn('[AnswerService] userAnswer is undefined/null for question:', question._id, question);
      }

      console.log('[AnswerService] Transforming question:', {
        questionId: question._id,
        userAnswer,
        solution,
        isCorrect,
        questionType: question.type
      });

      return {
        uid: user.uid,
        exerciseId: effectiveExerciseId,
        questionId: question._id,
        question: question.question,
        solution: solution,
        solutionString: getDisplaySolution(question),
        userAnswer: userAnswer,
        isCorrect: isCorrect,
        // Add quiz index for dialog exercises
        quizIndex: (question as any).quizIndex || undefined
      };
    }).filter(q => q !== null);

    if (transformedQuestions.length === 0) {
      console.error('[AnswerService] No valid questions to submit');
      return;
    }

    const payload = {
      questions: transformedQuestions,
      assignmentId
    };

    console.log('[AnswerService] Submitting answers:', {
      questionCount: transformedQuestions.length,
      assignmentId,
      exerciseId: effectiveExerciseId
    });

    await this.http.post<IExerciseQuestionType[]>(`answers`, payload).toPromise();
  }

  public async getAnswersForUser(userId: string): Promise<Answers[]> {
    const response = await this.http.get<Answers[]>(`answers/user/${userId}`).toPromise();
    return response || [];
  }

  public async getAnswersForAssignment(assignmentId: string): Promise<Answers[]> {
    const response = await this.http.get<Answers[]>(`answers/assignment/${assignmentId}`).toPromise();
    return response || [];
  }

  public async getDialogAnswer(exerciseId: string, assignmentId: string): Promise<any> {
    const response = await this.http.get<any>(`answers/dialog/${exerciseId}/${assignmentId}`).toPromise();
    return response;
  }

  /**
   * Initialize a Realtime API session for voice conversation
   */
  public async initializeRealtimeSession(answerId: string): Promise<RealtimeSessionConfig> {
    const response = await this.http.post<RealtimeSessionConfig>(
      `answers/dialog-interactive/${answerId}/realtime-session`,
      {}
    ).toPromise();
    
    if (!response) {
      throw new Error('Failed to initialize realtime session');
    }
    
    return response;
  }

  /**
   * Save voice transcript to backend
   */
  public async saveVoiceTranscript(
    answerId: string, 
    transcript: VoiceTranscript[], 
    duration?: number,
    options?: { rawSegments?: RawVoiceSegment[] }
  ): Promise<void> {
    const payload: any = {
      transcript: transcript.map(t => ({
        role: t.role,
        content: t.content,
        timestamp: t.timestamp.toISOString(),
        rawSegments: t.rawSegments,
        formatted: t.formatted,
        confidence: t.confidence,
        metadata: t.metadata,
      })),
      duration: duration
    };

    if (options?.rawSegments?.length) {
      payload.rawSegments = options.rawSegments;
    }

    await this.http.post(
      `answers/dialog-interactive/${answerId}/voice-transcript`,
      payload
    ).toPromise();
  }

  public async uploadVoiceSegment(
    answerId: string,
    payload: VoiceSegmentUploadPayload
  ): Promise<VoiceSegmentTranscriptionResponse> {
    const response = await this.http.post<VoiceSegmentTranscriptionResponse>(
      `answers/dialog-interactive/${answerId}/voice-segment`,
      payload
    ).toPromise();

    if (!response) {
      throw new Error('Failed to transcribe voice segment');
    }

    return response;
  }

  /**
   * Get interactive dialog history
   */
  public async getInteractiveDialogHistory(answerId: string): Promise<any> {
    const response = await this.http.get<any>(`answers/dialog-interactive/${answerId}`).toPromise();
    return response;
  }

  /**
   * Get assignment progress and calculate completion percentage
   */
  public async getAssignmentProgress(assignmentId: string): Promise<{ progress: number, answeredQuestions: number, totalQuestions: number }> {
    try {
      const answers = await this.getAnswersForAssignment(assignmentId);
      const answeredQuestions = answers.length;
      
      // Get the assignment details directly from the API
      const user = await this.userService.getUser();
      if (!user) {
        return { progress: 0, answeredQuestions: 0, totalQuestions: 0 };
      }
      
      // Get assignments directly from API instead of using AssignmentService
      const assignmentsResponse = await this.http.get<{user: any, assignments: any[]}>(`user/assignments`).toPromise();
      const assignment = assignmentsResponse?.assignments?.find(a => a._id === assignmentId);
      const totalQuestions = assignment?.questionCount || 0;
      
      const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
      
      return {
        progress: Math.round(progress),
        answeredQuestions,
        totalQuestions
      };
    } catch (error) {
      console.error('Error getting assignment progress:', error);
      return { progress: 0, answeredQuestions: 0, totalQuestions: 0 };
    }
  }

  public async getDialogAttemptStatus(
    exerciseId: string,
    assignmentId?: string,
    exerciseType?: string
  ): Promise<DialogAttemptStatus | null> {
    if (!exerciseId) {
      return null;
    }

    const params: any = { exerciseId };
    if (assignmentId) {
      params.assignmentId = assignmentId;
    }
    if (exerciseType) {
      params.exerciseType = exerciseType;
    }

    try {
      return await this.http.get<DialogAttemptStatus>('answers/dialog/attempts', { params }).toPromise();
    } catch (error) {
      console.error('Error fetching dialog attempt status:', error);
      return null;
    }
  }
}
